import { useCurrentTenant } from "./useCurrentTenant";
import { useJobTypes } from "@/features/admin/hooks/useJobTypes";

/**
 * R-146 — read the tenant's per-surface display templates.
 *
 * Backed by `useCurrentTenant`, which fetches `/api/me/tenant` once per
 * component mount. The Suite-side tenant id and name are already on that
 * response; the three template fields ride along, so this hook adds no
 * additional network traffic.
 *
 * All three values are nullable. A null/empty template means "use the
 * built-in structured `displayId` verbatim" — the standard
 * `renderDisplayTemplate` helper handles that fallback.
 *
 * R-167 WU-7 — the rendered label is now frozen on every row at create
 * time, so the per-request `renderDisplayTemplate` path is gone. The hook
 * still exposes the raw template strings (for admin tooling) plus a
 * derived `hasAnyTemplate` flag that UI surfaces use to decide whether to
 * show the "Custom label not assigned" tooltip on null-state rows.
 *
 * R-185 — `hasAnyTemplate` originally only checked the three TENANT-level
 * fields, so tenants who configured templates exclusively at the JOB TYPE
 * level (the WU-6b override pattern) never saw the null-state ℹ tooltip.
 * The flag now also ORs in "any job type in this tenant has a template
 * configured", read from the existing `useJobTypes()` list (its items carry
 * the per-JT template columns). This is the broad form: the tooltip fires
 * whenever a template exists anywhere in the tenant, not strictly on the
 * specific batch's parent job type (the precise per-batch variant would
 * require widening the batch-list wire payload, which is out of scope here).
 */
export interface TenantDisplayTemplates {
  batch: string | null;
  document: string | null;
  page: string | null;
}

function nonEmpty(value: string | null | undefined): boolean {
  return !!(value && value.trim().length > 0);
}

export function useTenantDisplayTemplates(): {
  templates: TenantDisplayTemplates;
  /**
   * R-167 WU-7 — true iff the tenant has any of the three display templates
   * configured. Drives the inline `ℹ` tooltip rendered next to legacy rows
   * (customDisplayLabel === null) so operators can tell at a glance "this
   * batch predates the current template" vs "no template configured."
   */
  hasAnyTemplate: boolean;
  loading: boolean;
} {
  const { data, loading } = useCurrentTenant();
  const { items: jobTypes, isLoading: jobTypesLoading } = useJobTypes();
  const templates = {
    batch: data?.batchDisplayTemplate ?? null,
    document: data?.documentDisplayTemplate ?? null,
    page: data?.pageDisplayTemplate ?? null,
  };
  const tenantHasTemplate =
    nonEmpty(templates.batch) ||
    nonEmpty(templates.document) ||
    nonEmpty(templates.page);
  // R-185 — also true when any job type carries a template override.
  // Guard the list defensively: a consumer's `api.get('/job-types')` mock may
  // resolve a shape without `items`, leaving the real hook's state undefined.
  const anyJobTypeHasTemplate = (jobTypes ?? []).some(
    (jt) =>
      nonEmpty(jt.batchDisplayTemplate) ||
      nonEmpty(jt.documentDisplayTemplate) ||
      nonEmpty(jt.pageDisplayTemplate),
  );
  const hasAnyTemplate = tenantHasTemplate || anyJobTypeHasTemplate;
  return { templates, hasAnyTemplate, loading: loading || jobTypesLoading };
}
